import React, {useEffect} from 'react'
import loadable from '@loadable/component'
import {useLocation} from 'react-router-dom'
import Seo from '~/app/components/seo'

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {Content, isPreviewing} from '@builder.io/sdk-react'
import {customComponents, builderConfig} from 'hec/builder'
import {useFetchOneEntryWithListener} from 'hec/hooks/useFetchOneEntryWithListener'
import 'hec/builder/menuConfigs/defaultConfig'
import Skeleton from '@/components/skeleton'
import {getSeoPageData} from '~/app/utils/helpers'
import {handlePageView} from '~/app/utils/segment/eventHandlers'
import {getSegmentInstance} from '~/app/utils/segment/config'
import {pageCategories} from '~/app/utils/segment/constants'
import {useFetchHeader} from 'hec/hooks/useFetchHeader'
import {useFetchFooter} from 'hec/hooks/useFetchFooter'
import {PageContent} from './PageContent'
import {PlpPageContent} from './PlpPageContent'
import {useAppOrigin} from '~/app/hooks/use-app-origin'
import {useIsLearningCenter} from 'hec/hooks/learning-center/useIsLearningCenter'
import {AccountFormLabelsProvider} from 'hec/builder/hooks/useGetAccountFormData'
import {AccountDrawer} from '@/components/account-drawer'
import useCheckForSubdomain from 'hec/hooks/useCheckForSubdomain'
import {BuilderTpaLearningCenterConsent} from 'hec/builder/blocks/tpa-learning-center-consent/tpaLearningCenterConsent'
import useLearningCenterConsent from '~/app/hooks/use-learning-center-consent'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {checkForURLParams} from '../../utils/helpers'
import {isNonProduction} from 'hec/utils/common'

const PageNotFound = loadable(() => import('@salesforce/retail-react-app/app/pages/page-not-found'))

export const Page = () => {
    const location = useLocation()
    const config = getConfig()
    const analytics = getSegmentInstance()
    const isPreview = isPreviewing()
    const appOrigin = useAppOrigin()
    const isLearningCenter = useIsLearningCenter()
    const {data: currentCustomer} = useCurrentCustomer()
    const {hasRespondedtoConsentModal} = useLearningCenterConsent()

    const urlPath = location.pathname

    const {data, isLoading, isError} = useFetchOneEntryWithListener({
        queryKey: ['Builder-Fetch-marketing', urlPath],
        options: {
            model: builderConfig.pageModel,
            userAttributes: {urlPath},
            apiKey: config.app.builder.api
        }
    })

    const {data: pageFieldData} = data || {}
    // Suppress header/footer: in prod check for TPA subdomain, in non-prod check for test-env param
    const isTpaSubdomain = useCheckForSubdomain('tpa')
    const shouldSuppressHeaderFooter = isNonProduction
        ? checkForURLParams('test-env')
        : isTpaSubdomain

    const {useDefaultHeader, useDefaultFooter, isPlpPage, productCategory} = pageFieldData || {}
    // This is where the header is requested, we always request because it's not commonly unused
    // It was considered a performance improvement to just make the request so it can be more likely cached.
    const {data: header} = useFetchHeader({
        name: 'Full Header',
        shouldQuery: useDefaultHeader && !shouldSuppressHeaderFooter
    })
    const {data: footer} = useFetchFooter({
        name: 'Main Footer',
        shouldQuery: useDefaultFooter && !shouldSuppressHeaderFooter
    })

    const seoData = getSeoPageData(pageFieldData)
    useEffect(() => {
        if (pageFieldData?.title && analytics && !isPreview) {
            handlePageView(analytics, {
                title: pageFieldData.title,
                category: isLearningCenter ? pageCategories.learningCenter : pageCategories.page,
                path: location.pathname,
                url: `${appOrigin}${location.pathname}`,
                search: location.search,
                referrer: document.referrer
            })
        }
    }, [location, pageFieldData?.title, analytics, isPreview, appOrigin, isLearningCenter])

    if (isLoading) {
        return (
            <div className="min-h-full">
                <Skeleton className="h-3/4 w-full" />
            </div>
        )
    }

    if (!isPreviewing(location.pathname) && isError) {
        return <PageNotFound />
    }

    const isCategoryPage = isPlpPage && !!productCategory

    let PageContentComponent = PageContent
    if (isCategoryPage) {
        PageContentComponent = PlpPageContent
    }

    return (
        <AccountFormLabelsProvider data={data.data.accountFormLabels}>
            <AccountDrawer />
            <div className="min-h-full" data-isSubdomain={isTpaSubdomain}>
                <Seo {...seoData} />
                {useDefaultHeader && !shouldSuppressHeaderFooter && (
                    <Content
                        model={builderConfig.header}
                        content={header}
                        apiKey={config.app.builder.api}
                        enrich={false}
                        customComponents={customComponents}
                    />
                )}

                <PageContentComponent data={data} />
                {!hasRespondedtoConsentModal() && currentCustomer?.c_TPA && isLearningCenter && (
                    <BuilderTpaLearningCenterConsent />
                )}
                {useDefaultFooter && !shouldSuppressHeaderFooter && (
                    <Content
                        model={builderConfig.footer}
                        content={footer}
                        apiKey={config.app.builder.api}
                        enrich={false}
                        customComponents={customComponents}
                    />
                )}
            </div>
        </AccountFormLabelsProvider>
    )
}

export default Page
